import * as _ from 'lodash-es';

// Logging Setup
import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { logs } from '@opentelemetry/api-logs';

// Tracing Setup
import customSpanProcessor from './custom-processor';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';

// Metrics Setup
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { OTLPMetricExporter, AggregationTemporalityPreference } from '@opentelemetry/exporter-metrics-otlp-http';
import { metrics } from '@opentelemetry/api';

// Shared
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';

// Application metrics
import { initMetrics } from './metrics';
import { initWebVitals } from './web-vitals';

// Service version
import { serviceVersion } from './version';

// Resource configuration
const resource = resourceFromAttributes({
  [ATTR_SERVICE_NAME]: import.meta.env.VITE_OTEL_SERVICE_NAME,
  [ATTR_SERVICE_VERSION]: serviceVersion,
  'deployment.environment': import.meta.env.VITE_OTEL_DEPLOYMENT_ENVIRONMENT
});

// _.isEqual (not ===) so the CI env-placeholder build can't constant-fold these gates:
// a plain === against the baked "env:ENABLE_OTEL_*" placeholder can be inlined and folded
// to false at build time, dead-code-eliminating the signal setup before entrypoint.sh
// substitutes the real value at container start.
const isEnabled = (value: string | undefined) => _.isEqual(value, 'true');

// No exporter endpoint configured → don't set up any OpenTelemetry at all.
// No providers registered, no instrumentation, no fallback endpoint.
const endpoint = import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT;
if (!endpoint) {
  console.warn('[vue:telemetry] otelExporterEndpoint not set — OpenTelemetry disabled');
}

// ============================================================================
// Logging Configuration
// ============================================================================

if (endpoint && isEnabled(import.meta.env.VITE_RESOURCE_ENABLE_OTEL_LOGGING)) {
  const loggerProvider = new LoggerProvider({
    resource,
    processors: [
      new BatchLogRecordProcessor(
        new OTLPLogExporter({
          url: `${endpoint}/v1/logs`
        })
      )
    ]
  });

  logs.setGlobalLoggerProvider(loggerProvider);
}

// ============================================================================
// Tracing Configuration
// ============================================================================

if (endpoint && isEnabled(import.meta.env.VITE_RESOURCE_ENABLE_OTEL_TRACING)) {
  const traceExporter = new OTLPTraceExporter({
    url: `${endpoint}/v1/traces`
  });

  const exportProcessor = new BatchSpanProcessor(traceExporter);

  const tracerProvider = new WebTracerProvider({
    resource,
    spanProcessors: [exportProcessor, customSpanProcessor]
  });

  tracerProvider.register({
    contextManager: new ZoneContextManager()
  });

  // The BatchSpanProcessor exports every 5 seconds and uses fetch with keepalive
  // (which survives page unload). The reference-counting in router.ts ensures
  // PAGE spans stay open until all child HTTP spans complete, so no spans are
  // orphaned. No additional flush listeners needed — the batch timer and
  // keepalive handle delivery reliably.
}

// NOTE: FetchInstrumentation is intentionally NOT used here.
// The Axios interceptor (interceptor.ts) handles span creation with correct
// PAGE span parenting via getPageContext(). FetchInstrumentation creates
// duplicate, unparented spans because it can't see the page span context.

// ============================================================================
// Metrics Configuration
// ============================================================================

if (endpoint && isEnabled(import.meta.env.VITE_RESOURCE_ENABLE_OTEL_METRICS)) {
  // DELTA temporality is required for SigNoz here. SigNoz derives rate/increase from the
  // delta between samples in the query window. With the OTLP default (CUMULATIVE) a browser
  // session's startup burst (init handshake + initial page loads) all accrues before the
  // first 60s export, so the first sample SigNoz sees already carries the full running total
  // and every increment before it is invisible to rate/increase — dashboards read ~0 even
  // though the counter climbed (observed: counter at 19, rate/increase computed ~2). With
  // DELTA each export carries only the change since the previous one, so SigNoz sums them
  // correctly and the whole session shows up.
  const metricReader = new PeriodicExportingMetricReader({
    exporter: new OTLPMetricExporter({
      url: `${endpoint}/v1/metrics`,
      temporalityPreference: AggregationTemporalityPreference.DELTA
    }),
    exportIntervalMillis: 60000 // Export metrics every 1 minute
  });

  // Initialize a MeterProvider with the above configurations.
  const myServiceMeterProvider = new MeterProvider({
    resource,
    readers: [metricReader]
  });

  // Set the initialized MeterProvider as global to enable metric collection across the app.
  metrics.setGlobalMeterProvider(myServiceMeterProvider);

  initMetrics();
  initWebVitals();
}
